/**
 * The wire contract, mirrored from `server/stagepost_server/schemas.py`.
 *
 * Hand-written rather than generated, and kept honest by a test: the committed
 * `openapi.json` is compared against these, so a change on the server that the
 * client has not been told about fails the build rather than the browser.
 *
 * The one rule worth remembering: a message on the wire is a superset of a
 * message on disk, and `sender` is spelled `sender` — it used to be `from`,
 * which is a keyword in both languages and caused nothing but trouble.
 */

export type SenderType = 'agent' | 'human' | 'event'

export interface EventDetails {
  title: string
  meta: string
  additions: string
  deletions: string
  url: string | null
  /** The branch a GitHub merge or push was about: a merged pull request's head, or the pushed ref. */
  branch: string | null
  /**
   * Present only when two branches are heading for the same file. Named on the
   * event rather than inferred from the title, because it is the one event that
   * asks a person to decide something and the row has to be able to tell.
   */
  overlap: { agents: string[]; paths: string[] } | null
  /**
   * On a status change, the sections it left empty: the title names what moved
   * and not which way. Null on events stored before it was recorded.
   */
  cleared: string[] | null
  /**
   * The agent run this event starts or follows, with the run's state when the
   * event was posted. Null on events stored before it was recorded. Optional
   * here only so fixtures written before it existed still type-check.
   */
  run?: RunEvent | null
}

export interface RunEvent {
  id: string
  state: string
  machine: string | null
  branch: string | null
  /** Where the harness put the session, as the run last reported it. Absent on older events. */
  harness_state?: string | null
  /** The agent whose proposal the person started, on the start event only. Absent on older events. */
  proposed_by?: string | null
}

/** A room named from another: an agent's home when it visits, or where its run started. */
export interface RoomReference {
  channel_id: string
  repository_key: string
}

export interface Message {
  id: string
  posted_at: string
  sender: string
  body: string
  /** The one-line summary, computed by the server. Empty from an older server. */
  headline: string
  reply_to: string | null
  sender_type: SenderType
  mentions: string[]
  event: EventDetails | null
  /**
   * Who `reply_to` answers and what they said, when that message is in the
   * room. Null on a root and on a reply whose parent is gone. Optional here
   * only so fixtures written before it existed still type-check.
   */
  reply_to_preview?: ReplyPreview | null
  replies: Message[]
  reactions: Reaction[]
  /** The answers of a question, drawn as buttons under it. Null on any other message. */
  options: string[] | null
  /** The option the first reply from a person chose, once one did. */
  answered_with: string | null
  /** Who a thread reply reached without a tag. Empty on a first message. */
  reaches: string[]
  /** A reply its sender also sent to the room. */
  shown_in_room: boolean
  /** What an agent sender calls itself now, next to the handle in `sender`. Null for a person and for an agent without one. */
  sender_nickname: string | null
  /**
   * When its sender last replaced the body; null when never. Optional here
   * only so fixtures written before it existed still type-check.
   */
  edited_at?: string | null
  /** The home room of an agent that posted this while visiting the room. Optional for older fixtures. */
  visiting_from?: RoomReference | null
}

export interface ReplyPreview {
  sender: string
  headline: string
}

/**
 * The five reactions, in the order the picker offers them.
 *
 * The name is what is stored and what an agent passes; the emoji is what a
 * reader sees. Keeping them apart is not decoration: the warning sign is U+26A0
 * followed by U+FE0F and plain U+26A0 is the same sign without the variation
 * selector, so a column of characters holds two spellings of one reaction.
 */
export const REACTION_EMOJI = {
  received: '\u{1F4E5}',
  seen: '\u{1F440}',
  done: '\u2705',
  agreed: '\u{1F44D}',
  disagree: '\u{1F44E}',
  conflict: '\u26A0\uFE0F',
} as const

export type ReactionName = keyof typeof REACTION_EMOJI

/** What each one claims, shown on hover so the set teaches itself. */
export const REACTION_MEANING: Record<ReactionName, string> = {
  received: 'Reached the agent, which will read it at its next step',
  seen: 'Read it, nothing here for me to do',
  done: 'I did the thing this asked for',
  agreed: 'I agree, or go ahead',
  disagree: 'Do not do this',
  conflict: 'This collides with what I am doing',
}

/** One reaction on a message, with everyone holding it. */
export interface Reaction {
  reaction: ReactionName
  count: number
  actors: string[]
}

export interface MessagePage {
  messages: Message[]
  latest_id: string | null
}

/**
 * One search result: a headline, and why it matched.
 *
 * Deliberately not a `Message`: search never returns a body, so widening the
 * type everything else uses would let a body slip back in by accident.
 * `lexical` is an exact token match, `semantic` a meaning match, and `both`
 * outranks either alone.
 */
export interface SearchHit {
  id: string
  posted_at: string
  sender: string
  sender_type: SenderType
  headline: string
  reply_to: string | null
  /** As on `Message`: what an agent sender calls itself now. */
  sender_nickname: string | null
  score: number | null
  found_by: 'lexical' | 'semantic' | 'both'
}

/**
 * An organisation's own pool of faces, expanded. `slots` is sparse: a pool
 * may fill eight of the thirty-six slots, and a slot it does not fill draws
 * `fallback_style` instead. See primitives/faces.ts, which is where a
 * `face:NN` key is turned into one of these entries or the fallback.
 */
export interface FaceSet {
  slug: string
  display_name: string
  fallback_style: string
  slots: { slot: number; url: string; name: string }[]
}

export interface Organisation {
  id: string
  slug: string
  name: string
  short_label: string
  retention_days: number
  /**
   * Which pool a `face:NN` slot resolves against: one of the five built-ins,
   * or `set:<slug>` naming the organisation's own pool. See
   * primitives/faces.ts.
   */
  face_style: string
  /** `face_style` expanded, when it names an organisation's own pool. Null when it names a built-in. */
  face_set: FaceSet | null
  role: string
  unread: number
}

export interface Channel {
  id: string
  repository_key: string
  organisation_slug: string
  unread: number
  online: number
  last_message_at: string | null
  github_full_name: string | null
  /** How far you had read when this was fetched. Empty for a room never opened. */
  read_cursor: string
  /**
   * When this room's Blocked section was last written, or null for a room with
   * none. The rail draws the same accent dot the room's own Status button
   * draws — see `buttonState` — so a blocker is visible from the room you are
   * not in, which is the only room it needed to be visible from.
   */
  blocked_since: string | null
  /** The newest message in it you have not read, from the organisation stream; the fetched list has none. */
  newest_unread?: UnreadLine | null
}

/** What a running agent's newest session is doing: between turns, in one, or held by a permission prompt. */
export type Activity = 'idle' | 'working' | 'blocked'

export interface Agent {
  name: string
  /** What the agent calls itself; `name` stays its handle. */
  nickname: string | null
  owner: string | null
  role: string | null
  biography: string | null
  summary: string | null
  /** The title of its newest session, as the app shows it. Only the owner is sent it. */
  title: string | null
  /**
   * When `summary` last changed. Null for an agent that has never said what it
   * is on. The roster renders the summary as a live status line, so this is
   * what separates "blocked on Dokploy" said just now from the same words said
   * three hours ago.
   */
  summary_updated_at: string | null
  branch: string | null
  /** The folder its newest session runs in, never the path. */
  worktree: string | null
  avatar_key: string | null
  online: boolean
  /** Null when it is not running; optional only for an older server. */
  activity?: Activity | null
  /** When its session said so. */
  activity_since?: string | null
  /** Repository-relative paths this agent is holding right now. */
  claims: string[]
  /** Where the newest live session runs, in the words to show ("Claude app", "terminal"). */
  surface: string | null
  /** A link that opens that session where it runs. */
  open_url: string | null
  /** A link that opens that session from claude.ai/code and the phone, when it is on Remote Control. */
  remote_url: string | null
  /** The run, from any room of the organisation, that became one of the agent's sessions. */
  started_by_run: RoomReference | null
  /**
   * The home room of an agent visiting this one, which the room lists apart
   * and leaves out of its own count. Optional for older fixtures.
   */
  visiting_from?: RoomReference | null
}

/** One path an agent has told the room it is working. */
export interface Claim {
  agent: string
  path: string
  intent: string | null
  claimed_at: string
}

/** A claim that overlaps one of yours, and which of yours it overlaps. */
export interface ClaimOverlap extends Claim {
  against: string
}

export interface ClaimResult {
  claims: Claim[]
  overlaps: ClaimOverlap[]
}

/** One current section of a room's status. */
export interface StatusSection {
  section: 'now' | 'blocked' | 'up_next'
  body: string
  author: string
  author_type: string
  /** Per section, not per document: the age of Blocked is the fact worth seeing. */
  updated_at: string
}

export interface RoomStatus {
  /** In reading order, and only the sections somebody has written. */
  sections: StatusSection[]
  /** The freshest section's timestamp, or null for a room with no status. */
  updated_at: string | null
}

/** Every reaction one message holds now, `[]` once the last is taken back. */
export interface MessageReactions {
  message_id: string
  reactions: Reaction[]
}

/** One step of an agent's plan, in the agent's own words. */
export interface PlanItem {
  text: string
  state: 'pending' | 'doing' | 'done'
}

/** What one running agent says it is working through, in its own order. */
export interface AgentPlan {
  agent: string
  items: PlanItem[]
  updated_at: string
}

/** One frame of a room's event stream. `synced` ends a connection's replay and first snapshots. */
export type RoomFrame =
  | { name: 'message'; data: Message }
  | { name: 'reactions'; data: MessageReactions }
  /** A message its sender edited, whole. */
  | { name: 'edited'; data: Message }
  | { name: 'presence'; data: Agent[] }
  | { name: 'claims'; data: Claim[] }
  | { name: 'status'; data: RoomStatus }
  | { name: 'plans'; data: AgentPlan[] }
  | { name: 'lessons'; data: LessonsSummary }
  /** Only on a stream that names it in `events`. */
  | { name: 'direct'; data: DirectMessage }
  | { name: 'synced'; data: Record<string, never> }

/** One unread message, as a Home row's second line shows it. */
export interface UnreadLine {
  id: string
  sender: string
  sender_type: SenderType
  sender_nickname: string | null
  headline: string
}

/** The first message of the thread a reply is in, as the Unreads list names it. */
export interface ThreadLine {
  id: string
  sender: string
  headline: string
}

/** A room message in the Unreads list. */
export interface UnreadMessage extends Message {
  /** The first message of its thread, when the room still holds it. Null on a first message. */
  thread: ThreadLine | null
}

/** One place in the Unreads list: a room, or the private messages of one of your agents. */
export type UnreadPlace = {
  /** Everything unread in the place, and how many of those the list leaves out. */
  unread: number
  earlier: number
} & (
  | {
      kind: 'direct'
      agent: string
      nickname: string | null
      avatar_key: string | null
      channel_id: null
      repository_key: null
      messages: []
      /** Its newest unread private messages, oldest first. */
      direct_messages: DirectMessage[]
    }
  | {
      kind: 'room'
      agent: null
      nickname: null
      avatar_key: null
      channel_id: string
      repository_key: string
      /** Its newest unread messages, oldest first. */
      messages: UnreadMessage[]
      direct_messages: []
    }
)

/** Your agents' private messages first, then rooms, each by its newest unread message, newest first. */
export interface UnreadPage {
  places: UnreadPlace[]
}

/** Your floor in a room, and the ids of the messages seen above it, sorted. */
export interface ReadState {
  read_through: string
  seen: string[]
}

/** One room as the organisation stream lists it. */
export interface OrganisationRoom {
  channel_id: string
  unread: number
  blocked_since: string | null
  last_message_at: string | null
  /** The newest message in the room you have not read. */
  newest_unread: UnreadLine | null
}

/** How far through its plan an agent is. */
export interface PlanProgress {
  done: number
  total: number
}

/** One of your own agents, as the organisation stream's `agents` frame lists it. */
export interface OrganisationAgent {
  name: string
  nickname: string | null
  avatar_key: string | null
  summary: string | null
  /** The title of its newest live session; the row shows it before the summary. */
  title: string | null
  /** The room of its newest live session. */
  channel_id: string | null
  repository_key: string | null
  online: boolean
  activity?: Activity | null
  activity_since?: string | null
  surface?: string | null
  open_url?: string | null
  remote_url?: string | null
  started_by_run: boolean
  plan: PlanProgress | null
  /** How many things it waits on you for. */
  waiting: number
  /** Its private messages you have not read, and the newest of them. */
  direct_unread: number
  newest_direct_unread: UnreadLine | null
}

/** One of your machines, as the organisation stream's `machines` frame lists it. */
export interface OrganisationMachine {
  id: string
  name: string
  default: boolean
  runner_online: boolean
  /** When the runner was last seen; null while it is online or if it never ran. */
  runner_off_since: string | null
  /** What a run would start in, or null when the runner reported none. */
  permission_mode: string | null
}

/** A private message between one of your agents and you. */
export interface DirectMessage {
  id: string
  agent: string
  posted_at: string
  /** The agent's name or your display name. */
  sender: string
  sender_type: 'human' | 'agent'
  body: string
  headline: string
  reply_to: string | null
  options: string[] | null
  answered_with: string | null
  /** The room copy's id, when you also sent it to the agent's room. */
  also_posted_message_id: string | null
  /** Set when the agent proposes you start a run; its answers are then Start and Not now. */
  proposed_run: ProposedRun | null
}

/** A run an agent proposes you start, in a room you read. */
export interface ProposedRun {
  room: string
  repository_key: string
  task: string
  /** After this, Start no longer starts it. */
  expires_at: string
}

export interface DirectMessagePage {
  messages: DirectMessage[]
  latest_id: string | null
}

/** One thing said between you and your agent: privately, or in a room. */
export type ConversationItem =
  | { place: 'direct'; room: null; channel_id: null; message: DirectMessage }
  | { place: 'room'; room: string; channel_id: string; message: Message }

export interface ConversationPage {
  /** Oldest first. */
  items: ConversationItem[]
  /** Pass as `before` for the page before this one; null on the first. */
  before: string | null
}

/** One frame of an organisation's event stream. `synced` ends a connection's first snapshots. */
export type OrganisationFrame =
  | { name: 'rooms'; data: OrganisationRoom[] }
  | { name: 'agents'; data: OrganisationAgent[] }
  | { name: 'machines'; data: OrganisationMachine[] }
  | { name: 'direct'; data: DirectMessage }
  | { name: 'synced'; data: Record<string, never> }

export interface StatusRevision {
  id: string
  section: string
  body: string
  author: string
  author_type: string
  written_at: string
  superseded_at: string | null
}

export interface SessionRow {
  session_key: string
  name: string
  working_directory: string | null
  branch: string | null
  state: string
  updated_at: string
}

/** One session as a span of the room's history: its first heartbeat and its last. */
export interface PresenceSpan {
  name: string
  branch: string | null
  arrived_at: string
  last_seen_at: string
  /** Not live now: it said so, or went quiet past the staleness window. */
  stopped: boolean
  /** How it stopped: the session-end hook said so, or it went quiet. Null while it is live. */
  ended: 'signed_off' | 'went_quiet' | null
}

/** One of your machines whose runner serves this room and is online. */
export interface RunTarget {
  id: string
  name: string
  default: boolean
}

export interface RunTargets {
  machines: RunTarget[]
  /** What to do about it when `machines` is empty, and null when it is not. */
  reason: string | null
}

/** An agent asked for from the web and started by a runner. */
export interface Run {
  id: string
  room: string
  repository_key: string
  state: string
  task: string
  machine: string
  branch: string
  handle: string | null
  harness_state: string | null
  error: string | null
  created_at: string
  started_at: string | null
  ended_at: string | null
  /** The trusted client that started it, such as Stageville; null from a browser. */
  started_from: string | null
  /** The face slot its session wears, chosen by the client; null when stagepost picks. */
  face_slot: number | null
}

export interface ActivityItem {
  reason: 'human' | 'mention'
  message_id: string
  channel_id: string
  repository_key: string
  organisation_slug: string
  sender: string
  posted_at: string
  preview: string
}

export interface NotifyPrefs {
  humans: boolean
  mentions: boolean
  all: boolean
}

/**
 * Which ways in this deployment has. GitHub sign-in needs an OAuth app, and a
 * server without one is a supported configuration — so the button is offered
 * only when the server says it will work.
 */
export interface SignInMethods {
  password: boolean
  github: boolean
  google: boolean
  /** Whether this deployment can mail a password link. False without Resend. */
  password_reset: boolean
  /** Whether a new account can be made here. False on an invitation-only server once it has an owner. */
  signup: boolean
}

export interface Me {
  id: string
  email: string
  display_name: string
  /** A pool slot or an uploaded key, resolved through primitives/faces.ts. */
  avatar_key: string | null
  notify: NotifyPrefs
  /** Whether a password would get you in — "set" versus "change" in Account. */
  has_password: boolean
  /** The linked GitHub account, or null. The login, never the id. */
  github_login: string | null
  /** The linked Google account's address, or null. Display only. */
  google_email: string | null
  /**
   * Where a new room lands when nothing else can place it — a slug, or null,
   * which is the normal state. Distinct from the organisation the rail is
   * showing: that is a browsing convenience kept in localStorage.
   */
  default_organisation: string | null
}

export interface Member {
  id: string
  display_name: string
  email: string
  role: string
  agent_count: number
  avatar_key: string | null
  pending: boolean
}

export interface MachineToken {
  id: string
  machine_name: string
  created_at: string
  last_used_at: string | null
  /** Space-separated scopes, or null for a token that reaches everything. */
  scope: string | null
}

/** An organisation grant: a client an owner connected the whole organisation to. */
export interface Integration {
  id: string
  client_name: string
  /** Space-separated organisation scopes. */
  scope: string
  /** The owner who approved it, or null once their account is gone. */
  created_by: string | null
  created_at: string
  last_used_at: string | null
}

export interface MintedToken extends MachineToken {
  /** The only time the plaintext is ever returned. */
  token: string
}

/** One GitHub App installation: an account, and what it was installed on. */
export interface GithubInstallation {
  installation_id: number
  account_login: string
  account_type: 'User' | 'Organization'
  account_avatar_url: string | null
  repository_selection: 'all' | 'selected'
  repository_count: number
  suspended: boolean
  /** Where the selection is changed. That screen is GitHub's, not ours. */
  manage_url: string
}

/**
 * One room in the GitHub pane.
 *
 * `linked` is whether an installation covers it. `enabled` is the mute, and it
 * is meaningful only when linked.
 */
export interface RepositoryRoom {
  repository_key: string
  github_full_name: string | null
  linked: boolean
  enabled: boolean
}

export interface GithubConnection {
  /** False when the server has no GitHub App configured at all. */
  configured: boolean
  installations: GithubInstallation[]
  rooms: RepositoryRoom[]
}

/**
 * Whose an item is, which is what decides whether its files may be published.
 *
 * A property of what is known about the item, never of its kind: a plugin
 * resolves by name because it has an origin, not because it is a plugin.
 * `origin_unknown` is the state that makes the whole feature safe, because
 * `license: MIT` says "not yours" without saying whose, and a guess about
 * whose would misattribute somebody's work.
 */
export type SetupState = 'has_origin' | 'yours' | 'origin_unknown'

/** One file of a published tree: a path relative to the item's own root. */
export interface PublicationFile {
  path: string
  content: string
}

/**
 * One thing a machine runs.
 *
 * `files` arrives only where a publication exists: the server holds no other
 * copy, so it is empty for everything its owner has not published, and for
 * everything in somebody else's record that they have not.
 */
export interface SetupItem {
  kind: string
  name: string
  origin: string | null
  coding_agent: string
  detail: Record<string, unknown> | null
  state: SetupState
  published: boolean
  /**
   * Which organisations read this item's files, as slugs, and the link if it
   * has one. Both are the owner's own view: reading somebody else's record
   * gets the files and learns nothing about who else has them.
   */
  audience: string[]
  shared_link: string | null
  /**
   * Whether the owner's record link carries this item.
   *
   * The owner's own view, like `audience` and `shared_link` above: reading
   * somebody else's record gets this back as false regardless of the truth.
   *
   * One at a time and never by omission: an item joins the link because
   * somebody named it, which is what keeps a "You alone" item from going
   * public in one click.
   */
  on_setup_link: boolean
  files: PublicationFile[]
}

/**
 * A record belongs to a machine, and a machine belongs to a person.
 *
 * `machine_identifier` is the identity and `name` is only a label. One laptop
 * called itself `MacBookPro` one afternoon and `Martins-MacBook-Pro.local` six
 * hours later, which made it two records; an id generated once and kept beside
 * the credential is what stops that. It is null for a record that reported
 * before the id existed.
 */
export interface Machine {
  /** The machine record's id, which `PUT /v1/me/default-machine` takes. */
  id: string
  name: string
  platform: string
  machine_identifier: string | null
  reported_at: string
  items: SetupItem[]
  /** The owner's own view only; false and null when a colleague reads it. */
  runner_online: boolean
  runner_seen_at: string | null
  /** Whether a run starts here when the person names no machine. */
  default: boolean
  /** The modes its runner will start an agent in. Empty for a runner that predates them. */
  permission_modes: string[]
  /** Modes that lift every check, which the runner was not installed to allow. */
  permission_modes_withheld: string[]
  /** What the runner starts in when nobody chose. */
  permission_mode_default: string | null
  /** The person's choice. Null is the runner's default, and so is a mode it stopped offering. */
  permission_mode: string | null
}

/** Keyed to the account and the item, never to the machine that sent the files. */
export interface Publication {
  kind: string
  name: string
  published_at: string
  file_count: number
}

/**
 * An origin somebody recorded by hand, which outlives any one machine.
 *
 * `mine` is the opposite claim: that they wrote it after all, which is the
 * only way an item leaves `origin_unknown`.
 */
export interface SetupOrigin {
  kind: string
  name: string
  origin: string | null
  mine: boolean
  recorded_at: string
}

/**
 * One organisation, and whether it may read this person's record at all.
 *
 * Per organisation because one person may be in a client's and their own. A
 * single boolean answering for every organisation at once meant publishing a
 * skill named after a client disclosed it to an employer, so the only safe
 * setting was off.
 */
export interface OrganisationSharing {
  organisation: string
  name: string
  enabled: boolean
}

export interface Setup {
  id: string
  display_name: string
  avatar_key: string | null
  /** Every organisation the owner is in. Empty on somebody else's record. */
  sharing: OrganisationSharing[]
  /**
   * One link to this whole record, where its owner has minted one.
   *
   * The owner's own view, like `SetupItem.on_setup_link`: reading somebody
   * else's record gets this back as null regardless of whether a link
   * exists, since a link is a capability rather than a fact about its owner,
   * and it is not something a colleague is owed.
   */
  setup_link: string | null
  machines: Machine[]
  publications: Publication[]
  origins: SetupOrigin[]
}

/** One publication, as somebody holding only its link reads it. */
export interface SharedPublication {
  kind: string
  name: string
  published_at: string
  display_name: string
  avatar_key: string | null
  files: PublicationFile[]
}

/** One carried item, as somebody holding a record link reads it. */
export interface SharedSetupItem {
  kind: string
  name: string
  origin: string | null
  published_at: string
  files: PublicationFile[]
}

/**
 * One person's carried setup, for whoever holds the link.
 *
 * The person and what they chose to carry, and nothing else of theirs: no
 * machines, no organisations, and no publication they did not put on the link.
 */
export interface SharedSetup {
  display_name: string
  avatar_key: string | null
  items: SharedSetupItem[]
}

/** One person in the organisation listing: who shares, and what they run it on. */
export interface SetupSummary {
  id: string
  display_name: string
  email: string
  avatar_key: string | null
  machines: string[]
  published_count: number
}

/**
 * One rule the organisation has learned.
 *
 * `always` is the small set injected into every session whatever it is doing;
 * everything else is retrieved when an agent says what it is about to do. That
 * split is the whole design, and it is why the pane shows the core apart from
 * the corpus rather than sorting one list by a flag.
 *
 * A superseded lesson is never deleted, so `superseded_by_id` is how the pane
 * can show what replaced a rule rather than losing the fact that it changed.
 */
export interface Lesson {
  id: string
  rule: string
  story: string
  category: string
  /** Where it was learned. Provenance; the rule itself applies everywhere. */
  repository_key: string | null
  author: string
  author_type: string
  learned_at: string
  always: boolean
  /** How often an agent hit this again and found it already adequate. */
  reinforced_count: number
  /** Present on a search result, absent on a plain listing. */
  score?: number | null
  superseded_by_id: string | null
  /** The rule this one sharpened. The pane follows it back to show earlier versions. */
  supersedes_id: string | null
}

/** A lesson among the latest learned or reinforced, as a room's stream names it. No rule: fetch the lesson for that. */
export interface RecentLesson {
  id: string
  author: string
  author_type: string
  repository_key: string | null
  learned_at: string
  reinforced_count: number
  last_reinforced_at: string | null
}

/** What a room's stream says about its organisation's lessons. Writing one posts nothing to any room. */
export interface LessonsSummary {
  /** Every lesson still standing. A superseded one is not counted. */
  total: number
  /** At most twenty, newest change first. */
  recent: RecentLesson[]
}
